import React from "react";
import { Avatar, Card, Space, Typography } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import TextRenderer from "../../utils/TextUtils/textRendererForLivePreview";
import { useTranslation } from "react-i18next";
import RatioBox, { useImageAspect, pickAspectFor } from "../RatioBox/RatioBox";

export default function FacebookPreview({ text = "", images = [], image, profileName, publisherName }) {
    const [expanded, setExpanded] = React.useState(false);
    const clamp = 420;

    const imgs = React.useMemo(() => {
        const raw = (images && images.length ? images : image ? [image] : []) || [];
        return raw
            .map((it) => (typeof it === "string" ? { url: it } : { url: it?.url }))
            .filter((i) => !!i.url);
    }, [images, image]);

    const count = imgs.length;
    const showCount = count > 4 ? 5 : count;
    const show = imgs.slice(0, showCount);
    const extra = Math.max(0, count - 4);

    const r0 = useImageAspect(show[0]?.url);
    const r1 = useImageAspect(show[1]?.url);

    const isWide = (r) => r && isFinite(r) && r >= 1.1;
    const isTall = (r) => r && isFinite(r) && r <= 0.9;

    const uname = profileName || "Business Page";
    const pby = publisherName || "Diffuzio-Dev";
    const initial = uname?.[0]?.toUpperCase() || "F";
    const { t } = useTranslation();


    const Tile = ({ url, ratio = "1/1", radius = 0, fit = "cover", children, style }) => (
        <RatioBox src={url} ratio={ratio} radius={radius} fit={fit} style={{ background: "transparent", ...style }}>
            {children}
        </RatioBox>
    );

    const Overlay = ({ n }) => (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 22 }}>+{n}</div>
    );

    return (
        <Card size="small" bordered bodyStyle={{ padding: 12 }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <Space align="center">
                    <Avatar size={36} style={{ background: "#1877f2" }}>{initial}</Avatar>
                    <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
                        <Typography.Text strong style={{ marginBottom: 2 }}>{uname}</Typography.Text>
                    </div>
                </Space>
                <EllipsisOutlined style={{ fontSize: 18, color: "#595959" }} />
            </div>

            {/* Text */}
            {text && (
                <Typography.Paragraph style={{ margin: "0 0 8px 0", lineHeight: 1.35 }}>
                    {expanded ? (
                        <>
                            <TextRenderer text={text} />
                            <Typography.Link onClick={() => setExpanded(false)} style={{ display: "block", marginTop: 4, padding: 0 }}>
                                {t("post.rollUp")}
                            </Typography.Link>
                        </>
                    ) : (
                        <>
                            <TextRenderer text={text} clampChars={clamp} />{" "}
                            {text.length > clamp && (
                                <Typography.Link onClick={() => setExpanded(true)} style={{ padding: 0 }}>
                                    {t("post.showMore")}
                                </Typography.Link>
                            )}
                        </>
                    )}
                </Typography.Paragraph>
            )}

            {/* Media */}
            <div style={{ width: "100%" }}>
                {count === 1 && (
                    <Tile url={show[0]?.url} ratio={pickAspectFor("facebook", r0) || "16/9"} fit="cover" />
                )}

                {count === 2 && (
                    isWide(r0) && isWide(r1) ? (

                        <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: 6 }}>
                            <Tile url={show[0]?.url} ratio={pickAspectFor("facebook", r0)} />
                            <Tile url={show[1]?.url} ratio={pickAspectFor("facebook", r1)} />
                        </div>
                    ) : (

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                            <Tile url={show[0]?.url} ratio="1/1" />
                            <Tile url={show[1]?.url} ratio="1/1" />
                        </div>
                    )
                )}

                {(count === 3 || count === 4) && (
                    isTall(r0) ? (
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "2fr 1fr",
                                gap: 6,
                                alignItems: "stretch",
                            }}
                        >
                            {/* Левая — высокая, задаёт высоту всего блока */}
                            <Tile
                                url={show[0]?.url}
                                ratio="3/4"
                                fit="cover"
                                style={{ height: "100%" }}
                            />

                            {/* Правая колонка — подгоняем ratio каждой плитки */}
                            {(() => {
                                const n = show.length - 1;
                                const rightRatio = `${3 * n}/8`;
                                return (
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateRows: `repeat(${n}, 1fr)`,
                                            gap: 6,
                                            height: "100%",
                                        }}
                                    >
                                        {show.slice(1).map((img, i) => (
                                            <Tile
                                                key={i}
                                                url={img?.url}
                                                ratio={rightRatio}
                                                fit="cover"
                                                style={{ height: "100%" }}
                                            />
                                        ))}
                                    </div>
                                );
                            })()}
                        </div>
                    ) : (
                        <>
                            <Tile url={show[0]?.url} ratio={pickAspectFor("facebook", r0) || "16/9"} style={{ marginBottom: 6 }} />
                            <div style={{ display: "grid", gridTemplateColumns: `repeat(${show.length - 1}, 1fr)`, gap: 6 }}>
                                {show.slice(1).map((img, i) => (
                                    <Tile key={i} url={img?.url} ratio="1/1" />
                                ))}
                            </div>
                        </>
                    )
                )}

                {count > 4 && (
                    <>
                        {/* две сверху — квадраты с кропом */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
                            <Tile url={show[0]?.url} ratio="1/1" />
                            <Tile url={show[1]?.url} ratio="1/1" />
                        </div>
                        {/* низ — три квадрата, на последней — оверлей */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                            {show.slice(2, 5).map((img, i) => (
                                <Tile key={i} url={img?.url} ratio="1/1">
                                    {i === 2 && extra > 1 ? <Overlay n={extra} /> : null}
                                </Tile>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </Card>
    );
}